import { AuthenticationResult, EventMessage, EventType, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { useEffect, useState } from 'react';
import { useAppDispatch } from '../store/hooks/hooks';
import { setSettings } from '@/store/slices/settingsSlice';
import { GlobalLoader } from './GlobalLoader';
import settingsSvc from '@/services/settings.service';
import App from '../App';
import NetworkStatus from './NetworkStatus';
import useInstallPrompt from '@/hooks/useInstallPrompt';

function Startup() {
  const { showPrompt, promptToInstall, dismissPrompt } = useInstallPrompt();

  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [scope, setScope] = useState("");
  const [settingsData, setSettingsData] = useState<any>(null);
  const dispatch = useAppDispatch();

  const getEntraIdData = (settings: any) => {
    const authData = settings.auth;
    return authData.find((item: any) => item.type === "EntraID");
  };

  const storeSettings = (settings: any) => {
    const pagination = settings?.pagination;
    if (pagination) {
      sessionStorage.setItem("Pagination", JSON.stringify(pagination));
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data } = await settingsSvc.get();
        if (!data) return;

        const entraIdData = getEntraIdData(data);
        storeSettings(data);
        dispatch(setSettings(data));
        setSettingsData(data);
        setScope(entraIdData.frontend.backendScope);

        const msalConfig = {
          auth: {
            clientId: entraIdData.frontend.clientId!,
            authority: entraIdData.frontend.authority,
            redirectUri: `${window.location.protocol}//${window.location.host}/sso`,
            postLogoutRedirectUri: "/",
          },
          cache: {
            cacheLocation: "localStorage",
            storeAuthStateInCookie: false,
          },
        };

        const instance = new PublicClientApplication(msalConfig);
        await instance.initialize();

        instance.addEventCallback((event: EventMessage) => {
          if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const result = event.payload as AuthenticationResult;
            instance.setActiveAccount(result.account);
          }
        });

        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          instance.setActiveAccount(accounts[0]);
        }

        instance.enableAccountStorageEvents();
        setMsalInstance(instance);
      } catch (error) {
        console.error("Startup initialization error:", error);
      }
    };

    initializeAuth();
  }, [dispatch]);

  if (!msalInstance || !settingsData) {
    return <GlobalLoader isStartup={true} />;
  }

  return (
    <MsalProvider instance={msalInstance}>
      {showPrompt && (
        <div className="install-banner">
          <p>Install this app for a better experience.</p>
          <button onClick={promptToInstall}>Install</button>
          <button onClick={dismissPrompt}>Close</button>
        </div>
      )}
      <NetworkStatus />
      <GlobalLoader isStartup={false} />
      <App msalInstance={msalInstance} msalScope={scope} settings={settingsData} />
    </MsalProvider>
  );
}

export default Startup;